//region :: Imports ::
import MComponent from '../Toolkit/Components/Component';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import WorkerService from '../Services/WorkerService';
import {createRef, Fragment, ReactElement, RefObject} from 'react';
import MForm, {MFormOutcome} from '../Toolkit/Components/Controls/Form';
import {Worker, WorkerValidator} from '../Services/WorkerHubTypes';
import {
    copyToClipboard,
    formatDateAsText,
    getEnumItems,
    hasRoles,
    MEnumItem,
    MNullable,
    toErrorMessage
} from '../Toolkit/Utils';
import {UpdateWorkerRequest, WorkerType} from '../Api/WorkerHubClients';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MButton from '../Toolkit/Components/Controls/Button';
import {CloseIconName, InfoIconName, SaveIconName} from '../Toolkit/Icons';
import WithRouter from '../Toolkit/WithRouter';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import MFormFieldControl from '../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../Toolkit/Components/Controls/TextBox';
import {Message} from 'primereact/message';
import MCopyButton from '../Toolkit/Components/Controls/CopyButton';
import MAddToSystemButton from './Components/AddToSystemButton';
import MFormSection from '../Toolkit/Components/Controls/FormSection';
import WorkerNhsService from '../Services/WorkerNhsService';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MPropertyList, {MPropertyListItem} from '../Toolkit/Components/Controls/PropertyList';
import MDynEntityLookup from "../Toolkit/Components/Controls/Dynamics/DynEntityLookup";
import {Roles} from "../Roles";
import MAuthProvider from "../Toolkit/Components/Auth/AuthProvider";

//endregion

/** */
type RouteParams = {
    workerId: string;
}

/** */
type WorkerFormData = {
    worker: Worker;
    workerTypeItem: {
        label: string;
        value: MNullable<WorkerType>;
    };
    resourceManager: string;
    organizationalUnit: string;
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    workerTypeItems: MDropDownItem<WorkerType>[];
    form: {
        id: string;
        data: WorkerFormData;
        isSubmitting: boolean;
        validationSchema: any;
    }
}

/** */
class EditWorkerPage extends MComponent<Props, State> {

    private readonly m_workerService: WorkerService;
    private readonly m_workerNhsService: WorkerNhsService;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_fieldServiceUrl: string;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_workerService = new WorkerService(this.props.msalContext);
        this.m_workerNhsService = new WorkerNhsService(this.props.msalContext);
        this.m_pageRef = createRef<MPage>();
        this.m_fieldServiceUrl = process.env.REACT_APP_D365_FIELDSERVICE_HOST_URL!;


        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.isFormDisabled = this.isFormDisabled.bind(this);
        this.isFormDisabledAccess = this.isFormDisabledAccess.bind(this);
        this.closeButton_onClick = this.closeButton_onClick.bind(this);
        this.copyIdButton_onClick = this.copyIdButton_onClick.bind(this);
        this.addToNhsButton_Click = this.addToNhsButton_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);

    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Worker Types
        const workerTypeItems = getEnumItems(WorkerType).map((item: MEnumItem) => {
            return {label: item.name, value: item.value};
        });

        //  Form Data
        const data: WorkerFormData = {
            worker: {} as Worker,
            workerTypeItem: {
                label: '',
                value: null
            },
            resourceManager: '',
            organizationalUnit: ''
        };

        //  Form Validation
        const validationSchema: any = Yup.object().shape({
            worker: Yup.object().shape({
                firstName: WorkerValidator.firstName,
                lastName: WorkerValidator.lastName,
                resourceManager: WorkerValidator.resourceManager,
                organizationUnitRef: WorkerValidator.organizationUnit
            })
        });

        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            workerTypeItems: workerTypeItems,
            form: {
                id: 'workerForm',
                data: data,
                isSubmitting: false,
                validationSchema: validationSchema
            }
        };

    }

    /** */
    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    /** */
    private isFormDisabledAccess(): boolean {
        if (!hasRoles([Roles.fullAccess], MAuthProvider.msalContext))
        {
            return true;
        }
        return this.state.form.isSubmitting;
    }

    /** */
    private setWorker(worker: Worker): void {

        this.safeSetState((draft: State) => {

            const workerTypeItem = this.state.workerTypeItems
                .find(wt => wt.value === worker.workerType)!;

            draft.pageTitle = `${worker.firstName} ${worker.lastName}`;
            draft.pageSubtitle = 'Edit Worker';
            draft.form.data = {
                worker: worker,
                workerTypeItem: workerTypeItem,
                organizationalUnit: worker.organizationUnitRef,
                resourceManager: worker.managerReference
            };

        });

    }

    /** */
    private closeButton_onClick(): void {
        this.props.history.goBack();
    }

    /** */
    private async copyIdButton_onClick(): Promise<void> {
        await copyToClipboard(this.state.form.data.worker.id);
    }

    /** */
    private async addToEnergyButton_onClick(): Promise<void> {
        this.props.navigate(`/workers/${this.props.match.params.workerId}/energy/add`);
    }

    /** */
    private async addToNhsButton_Click(): Promise<void> {
        this.props.navigate(`/workers/${this.props.match.params.workerId}/nhs/add`);
    }

    /** */
    private form_onBeforeSubmit(fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>): boolean {

        const cancel = false;
        this.m_pageRef.current!.messageBar.close();
        this.safeSetState((draft: State) => draft.form.isSubmitting = true);
        return cancel;

    }

    /** */
    private async form_onSubmit(fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>): Promise<void> {

        const result = await this.m_workerService.update(
            this.props.match.params.workerId,
            new UpdateWorkerRequest({
                firstName: fieldValues.worker.firstName,
                lastName: fieldValues.worker.lastName,
                organizationUnitRef: fieldValues.organizationalUnit,
                managerReference: fieldValues.resourceManager,
            }));

        if(result.hasErrors) {
            throw result.exception;
        }

        this.m_pageRef.current!
            .messageBar.success('Worker has been updated');
        this.setWorker(result.reply!.worker!);

    }

    /** */
    private form_onError(exception: any): boolean {

        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;

    }

    /** */
    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>, exception: any | undefined): void {

        this.safeSetState(
            (draft: State) => draft.form.isSubmitting = false);

    }

    /** */
    private async page_onLoadData(): Promise<void> {

        const workerId = this.props.match.params.workerId;
        const result = await this.m_workerService.get(workerId);
        const result2 = await this.m_workerService.getOrgDetails(workerId);
        const result3 = await this.m_workerService.getManagerDetails(workerId);

        if(result.hasErrors) {
            throw result.exception;
        }
        if(result2.hasErrors) {
            throw result.exception;
        }
        if(result3.hasErrors) {
            throw result.exception;
        }

        if(result.reply && result.reply.worker)
            result.reply.worker.organizationUnitRef = result2.reply?.lookup?.name || ''

        if(result.reply && result.reply.worker)
            result.reply.worker.managerReference = result3.reply?.lookup?.name || ''

        this.setWorker(result.reply!.worker!);

    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={SaveIconName}
                         iconOnly={true}
                         isBusy={this.isFormDisabled}
                         disabled={false}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={CloseIconName}
                         disabled={false}
                         onClick={this.closeButton_onClick}/>
            </MToolbar>
        );

    }

    /** */
    public render(): ReactElement {

        const worker = this.state.form.data.worker;
        const metadata: MPropertyListItem[] = [
            {label: 'Created By', value: worker.createdBy},
            {label: 'Created On', value: formatDateAsText(worker.createdOn!)},
            {label: 'Modified By', value: worker.modifiedBy},
            {label: 'Modified On', value: formatDateAsText(worker.modifiedOn!)}
        ];

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={9}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <div className="grid">
                    <div className="col-12 md:col-9">
                        <MForm formId={this.state.form.id}
                               initialValues={this.state.form.data}
                               validationSchema={this.state.form.validationSchema}
                               onBeforeSubmit={this.form_onBeforeSubmit}
                               onSubmit={this.form_onSubmit}
                               onError={this.form_onError}
                               onAfterSubmit={this.form_onAfterSubmit}>

                            <MFormSection title="General">

                                {/* Worker ID */}
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Worker ID"
                                                       fieldName="worker.id">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <Fragment>

                                                    <MTextBox id={fieldProps.field.name}
                                                              maxLength={4}
                                                              readonly={true}
                                                              fieldProps={fieldProps}
                                                              disabled={this.isFormDisabled}
                                                              className={fieldClasses}/>

                                                    <MCopyButton disabled={this.isFormDisabled}
                                                                 onClick={this.copyIdButton_onClick}/>

                                                </Fragment>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                {/* Padding */}
                                <div className="md:col-8"/>

                                {/* First Name */}
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="First Name"
                                                       fieldName="worker.firstName">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={100}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabledAccess}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                {/* Last Name */}
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Last Name"
                                                       fieldName="worker.lastName">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={100}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabledAccess}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                {/* Worker Type */}
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Worker Type"
                                                       fieldName="workerTypeItem.label">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={50}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Resource Manager"
                                                       fieldName="resourceManager">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MDynEntityLookup id={fieldProps.field.name}
                                                                  environmentUrl={this.m_fieldServiceUrl}
                                                                  entityLogicalName="bookableresource"
                                                                  searchFieldName="name"
                                                                  searchFilter="statuscode eq 1"
                                                                  authContext={this.props.msalContext}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>
                                {/* Organisation Unit */}
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Organisation Unit"
                                                       fieldName="organizationalUnit">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MDynEntityLookup id={fieldProps.field.name}
                                                                  environmentUrl={this.m_fieldServiceUrl}
                                                                  entityLogicalName="msdyn_organizationalunit"
                                                                  searchFieldName="msdyn_name"
                                                                  searchFilter="statuscode eq 1"
                                                                  authContext={this.props.msalContext}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>


                                {/* Info */}
                                <div className="col-12">
                                    <Message text="Extended worker information, i.e., Job Title, Address, Work Phone Number, etc, can be modified in the the relevant system."
                                             severity="info"
                                             icon={InfoIconName}
                                             className="w-full"/>
                                </div>

                            </MFormSection>

                            <MFormSection title="Systems">

                                {/* Systems */}
                                <div className="col-12">
                                    <div className="flex gap-2">
                                        <MAddToSystemButton systemName="D365 NHS"
                                                            existsInSystem={this.state.form.data.worker.existsInNhs}
                                                            disabled={this.isFormDisabled}
                                                            onClick={this.addToNhsButton_Click}/>
                                    </div>
                                </div>

                            </MFormSection>

                        </MForm>
                    </div>
                    <div className="col-12 md:col-3">
                        <MPropertyList header="Properties"
                                       items={metadata}/>
                    </div>
                </div>

            </MPage>
        );

    }
}

export default withMsal(WithRouter(EditWorkerPage));
